import {
  Chess,
  Swal,
  copyText,
  createChessground,
  createChessPiecePreview,
  observeBoardResize
} from './chess-ui.js';
import { castlingRightIsPossible, validatePosition } from './position-validation.js';

const roleToFen = Object.freeze({
  pawn: 'p',
  knight: 'n',
  bishop: 'b',
  rook: 'r',
  queen: 'q',
  king: 'k'
});

const root = typeof document === 'undefined' ? null : document.querySelector('[data-position-editor]');
if (root) {
  const initialFen = new Chess().fen();
  const boardElement = root.querySelector('#positionBoard');
  const output = root.querySelector('[data-fen-output]');
  const message = root.querySelector('[data-position-message]');
  const turnSelect = root.querySelector('[data-turn]');
  const enPassantInput = root.querySelector('[data-en-passant]');
  const handButtons = [...root.querySelectorAll('[data-hand-tool]')];
  const deleteButtons = [...root.querySelectorAll('[data-delete-piece]')];
  const startGameButton = root.querySelector('[data-start-position-game]');
  const analyzeButton = root.querySelector('[data-analyze-position]');
  let selectedPiece = null;
  let orientation = 'white';
  let updateFrame = 0;

  const ground = createChessground(boardElement, {
    fen: initialFen,
    orientation,
    coordinates: true,
    autoCastle: false,
    animation: { enabled: true, duration: 150 },
    premovable: { enabled: false },
    draggable: { enabled: true, deleteOnDropOff: true },
    movable: {
      free: true,
      color: 'both',
      events: { after: scheduleFenUpdate }
    },
    events: {
      change: scheduleFenUpdate,
      select: square => {
        if (selectedPiece === null) return;
        ground.setPieces(new Map([[
          square,
          selectedPiece === 'delete' ? undefined : { ...selectedPiece }
        ]]));
        scheduleFenUpdate();
      }
    }
  });

  observeBoardResize(ground, boardElement);
  renderPalette(root.querySelector('[data-palette="white"]'), 'white');
  renderPalette(root.querySelector('[data-palette="black"]'), 'black');
  renderMobilePalettes();

  handButtons.forEach(button => button.addEventListener('click', () => selectTool(button, null)));
  deleteButtons.forEach(button => button.addEventListener('click', event => selectTool(event.currentTarget, 'delete')));
  root.querySelector('[data-start-position]').addEventListener('click', () => loadFen(initialFen));
  root.querySelector('[data-clear-position]').addEventListener('click', clearBoard);
  root.querySelector('[data-flip-position]').addEventListener('click', () => {
    orientation = orientation === 'white' ? 'black' : 'white';
    ground.set({ orientation });
    renderMobilePalettes();
    selectTool(handButtons[0], null);
  });
  root.querySelector('[data-copy-fen]').addEventListener('click', () => copyText(output.value, 'FEN کپی شد'));
  analyzeButton.addEventListener('click', () => openPosition(root.dataset.analysisUrl || '/Tools/Analysis', false));
  startGameButton.addEventListener('click', () => openPosition(root.dataset.localGameUrl || '/Play/Local', true));
  output.addEventListener('change', () => loadFen(output.value));
  turnSelect.addEventListener('change', updateFen);
  enPassantInput.addEventListener('input', updateFen);
  root.querySelectorAll('[data-castle]').forEach(input => input.addEventListener('change', updateFen));

  function renderPalette(container, color) {
    if (!container) return;
    container.replaceChildren();
    const roles = ['king', 'queen', 'rook', 'bishop', 'knight', 'pawn'];
    for (const role of roles) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'palette-piece';
      button.title = `${color === 'white' ? 'سفید' : 'سیاه'} - ${role}`;
      button.setAttribute('aria-label', button.title);
      button.append(createChessPiecePreview({ color, role }));
      const piece = { color, role };
      button.addEventListener('click', () => selectTool(button, piece));
      container.append(button);
    }
  }

  function renderMobilePalettes() {
    const topColor = orientation === 'white' ? 'black' : 'white';
    const bottomColor = topColor === 'black' ? 'white' : 'black';
    renderMobilePalette('top', topColor);
    renderMobilePalette('bottom', bottomColor);
  }

  function renderMobilePalette(position, color) {
    const row = root.querySelector(`[data-mobile-palette="${position}"]`);
    if (!row) return;
    row.dataset.color = color;
    row.setAttribute('aria-label', `مهره‌های ${color === 'white' ? 'سفید' : 'سیاه'}`);
    renderPalette(row.querySelector('[data-mobile-palette-pieces]'), color);
  }

  function selectTool(button, piece) {
    root.querySelectorAll('.palette-piece, .palette-delete, .palette-hand').forEach(item => item.classList.remove('selected'));
    if (piece === null) handButtons.forEach(item => item.classList.add('selected'));
    else if (piece === 'delete') deleteButtons.forEach(item => item.classList.add('selected'));
    else button.classList.add('selected');
    selectedPiece = piece;
    ground.set({
      movable: {
        free: piece === null,
        color: piece === null ? 'both' : undefined
      }
    });
  }

  function clearBoard() {
    const changes = new Map(allSquares().map(square => [square, undefined]));
    ground.setPieces(changes);
    root.querySelectorAll('[data-castle]').forEach(input => { input.checked = false; });
    enPassantInput.value = '-';
    turnSelect.value = 'w';
    selectTool(handButtons[0], null);
    scheduleFenUpdate();
  }

  function loadFen(fen) {
    try {
      const normalized = fen.trim();
      const parts = normalized.split(/\s+/);
      if (parts.length !== 6) throw new Error('FEN باید شش بخش داشته باشد.');
      const parsed = new Chess(normalized);
      ground.set({ fen: parts[0] });
      turnSelect.value = parts[1];
      root.querySelectorAll('[data-castle]').forEach(input => {
        input.checked = parts[2].includes(input.dataset.castle);
      });
      enPassantInput.value = parts[3];
      output.value = normalized;
      applyValidation(validatePosition(normalized));
      selectTool(handButtons[0], null);
      requestAnimationFrame(() => ground.redrawAll());
    } catch (error) {
      showInvalidMessage(error.message);
    }
  }

  function scheduleFenUpdate() {
    cancelAnimationFrame(updateFrame);
    updateFrame = requestAnimationFrame(updateFen);
  }

  function updateFen() {
    syncCastlingControls();
    const boardFen = piecesToFen(ground.state.pieces);
    const castling = [...root.querySelectorAll('[data-castle]:checked')]
      .map(input => input.dataset.castle)
      .join('') || '-';
    const enPassant = /^(-|[a-h][36])$/.test(enPassantInput.value.trim())
      ? enPassantInput.value.trim()
      : '-';
    const fen = `${boardFen} ${turnSelect.value} ${castling} ${enPassant} 0 1`;
    output.value = fen;
    applyValidation(validatePosition(fen));
    return fen;
  }

  function applyValidation(validation) {
    startGameButton.disabled = !validation.canStart;
    analyzeButton.disabled = !validation.isLegal;
    startGameButton.setAttribute('aria-disabled', String(!validation.canStart));
    analyzeButton.setAttribute('aria-disabled', String(!validation.isLegal));

    message.classList.toggle('valid', validation.isLegal && validation.canStart);
    message.classList.toggle('warning', validation.isLegal && !validation.canStart);

    if (!validation.isLegal) {
      message.textContent = `پوزیسیون نامعتبر: ${validation.errors[0]}`;
      message.title = validation.errors.join(' | ');
      return;
    }
    if (!validation.canStart) {
      message.textContent = validation.warnings[0];
      message.title = validation.warnings.join(' | ');
      return;
    }
    message.textContent = 'پوزیسیون قانونی و آماده شروع بازی است';
    message.title = '';
  }

  function showValidMessage() {
    applyValidation(validatePosition(output.value));
  }

  function showInvalidMessage(text) {
    startGameButton.disabled = true;
    analyzeButton.disabled = true;
    message.textContent = `پوزیسیون نامعتبر: ${text}`;
    message.title = text;
    message.classList.remove('valid', 'warning');
  }

  function syncCastlingControls() {
    root.querySelectorAll('[data-castle]').forEach(input => {
      const possible = castlingRightIsPossible(ground.state.pieces, input.dataset.castle);
      if (!possible) input.checked = false;
      input.disabled = !possible;
      input.closest('.checkbox-row')?.classList.toggle('unavailable', !possible);
    });
  }

  async function openPosition(baseUrl, requirePlayable) {
    const fen = updateFen();
    const validation = validatePosition(fen);
    applyValidation(validation);
    if (!validation.isLegal || (requirePlayable && !validation.canStart)) {
      const details = validation.errors.length > 0 ? validation.errors : validation.warnings;
      await Swal.fire({
        icon: validation.isLegal ? 'warning' : 'error',
        title: requirePlayable ? 'پوزیسیون قابل شروع نیست' : 'پوزیسیون قابل آنالیز نیست',
        html: `<div class="position-error-list">${details.map(item => `<div>• ${escapeHtml(item)}</div>`).join('')}</div>`,
        confirmButtonText: 'باشه'
      });
      return;
    }
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('fen', fen);
    window.location.href = url.toString();
  }

  updateFen();
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('\"', '&quot;')
    .replaceAll("'", '&#039;');
}

export function piecesToFen(pieces) {
  const ranks = [];
  for (let rank = 8; rank >= 1; rank--) {
    let line = '';
    let empty = 0;
    for (let file = 0; file < 8; file++) {
      const square = `${String.fromCharCode(97 + file)}${rank}`;
      const piece = pieces?.get?.(square);
      const role = piece?.role ?? piece?.type;
      const symbol = roleToFen[role];
      if (!piece || !symbol) {
        empty++;
        continue;
      }
      if (empty > 0) line += empty;
      empty = 0;
      line += piece.color === 'white' || piece.color === 'w' ? symbol.toUpperCase() : symbol;
    }
    if (empty > 0) line += empty;
    ranks.push(line || '8');
  }
  return ranks.join('/');
}

function allSquares() {
  const squares = [];
  for (let rank = 1; rank <= 8; rank++) {
    for (let file = 0; file < 8; file++) squares.push(`${String.fromCharCode(97 + file)}${rank}`);
  }
  return squares;
}
